import { Alert, Box, Button, IconButton, Link, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import { DataTable } from '../../components/DataTable';
import { KeyValueList } from '../../components/KeyValueList';
import { useNotice } from '../../components/NoticeProvider';
import { StatusChip } from '../../components/StatusChip';
import { useAgents } from '../agents/useAgents';
import { useCarriers } from '../carriers/useCarriers';
import { useCustomers } from '../customers/useCustomers';
import { LOG_TONE, whenOf, TYPE_TONE, type LogEntry } from './sample';

/** Where the related company's own page is, if we have one */
function useRelatedLink(e: LogEntry) {
  const lists = { Agent: useAgents().items, Carrier: useCarriers().items, Customer: useCustomers().items };
  if (e.relatedKind === 'None') return '';
  const found = lists[e.relatedKind].find((x) => x.name.toLowerCase() === e.related.toLowerCase());
  const base = e.relatedKind === 'Agent' ? 'agents' : e.relatedKind === 'Carrier' ? 'carriers' : 'customers';
  return found ? `/${base}/${found.id}` : '';
}

const Heading = ({ children }: { children: string }) => (
  <Typography sx={{ fontWeight: 750, mt: 1 }}>{children}</Typography>
);

/** One log entry in full: who did it, what they did step by step, what was recorded, what changed, and what comes next */
export function ActivityDetail({ e, onClose }: { e: LogEntry; onClose: () => void }) {
  const notice = useNotice();
  const link = useRelatedLink(e);
  return (
    <Stack spacing={1.5} sx={{ p: { xs: 1.5, sm: 2.5 }, pt: 'max(16px, env(safe-area-inset-top))' }}>
      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" component="h2">
          Activity Details
        </Typography>
        <IconButton aria-label="Close" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Stack>

      <Box>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="h5" component="h3" sx={{ fontWeight: 750 }}>
            {e.activity}
          </Typography>
          <StatusChip label={e.status} tone={LOG_TONE[e.status]} />
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {whenOf(e)} (Jordan Time)
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Performed by <b>{e.by}</b> · <StatusChip label={e.type} tone={TYPE_TONE[e.type]} />
        </Typography>
      </Box>

      <Heading>Related Records</Heading>
      <Stack direction="row" spacing={3} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
        {e.srb && (
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Shipment
            </Typography>
            <Link component={RouterLink} to={`/shipments/${e.srb}`} underline="hover" sx={{ fontWeight: 600 }}>
              {e.srb}
            </Link>
          </Box>
        )}
        {e.related && (
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {e.relatedKind}
            </Typography>
            {link ? (
              <Link component={RouterLink} to={link} underline="hover" sx={{ fontWeight: 600 }}>
                {e.related}
              </Link>
            ) : (
              <Typography sx={{ fontWeight: 600 }}>{e.related}</Typography>
            )}
          </Box>
        )}
        {!e.srb && !e.related && (
          <Typography variant="body2" color="text.secondary">
            Not linked to a shipment.
          </Typography>
        )}
      </Stack>

      {e.steps && (
        <>
          <Heading>{e.by === 'Claude' ? 'What Claude Did' : 'What You Did'}</Heading>
          <Stack spacing={0.75} component="ol" sx={{ m: 0, pl: 2.5 }}>
            {e.steps.map((s) => (
              <Typography key={s} component="li" variant="body2">
                {s}
              </Typography>
            ))}
          </Stack>
        </>
      )}

      <Heading>Information Recorded</Heading>
      <KeyValueList
        rows={[
          ...(e.info ?? []).map(([label, value]) => ({ label, value })),
          ...(e.info ? [] : [{ label: 'Details', value: e.details }]),
        ]}
      />

      {e.changes && (
        <>
          <Heading>Changes Made</Heading>
          <DataTable<[string, string, string]>
            rows={e.changes}
            rowKey={(c) => c[0]}
            columns={[
              { header: 'Field', primary: true, render: (c) => c[0] },
              {
                header: 'Previous',
                render: (c) => (
                  <Typography component="span" variant="body2" color="text.secondary">
                    {c[1]}
                  </Typography>
                ),
              },
              { header: 'New', render: (c) => <b>{c[2]}</b> },
            ]}
          />
        </>
      )}

      {e.nextStep && (
        <>
          <Heading>Next Step</Heading>
          <Alert severity={e.status === 'Needs Attention' ? 'error' : 'warning'}>{e.nextStep}</Alert>
        </>
      )}

      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
        {e.srb && (
          <Button component={RouterLink} to={`/shipments/${e.srb}`} variant="contained">
            Open Shipment
          </Button>
        )}
        {e.type === 'Quotation' && e.srb && (
          <Button component={RouterLink} to={`/quotations/${e.srb}`} variant="outlined">
            View Quotation
          </Button>
        )}
        {e.status === 'Pending Approval' && (
          <Button component={RouterLink} to="/approvals" variant="outlined">
            Open Approvals
          </Button>
        )}
        {e.source && (
          <Button
            variant="outlined"
            onClick={() => notice('The source email opens once the mailbox is connected.', 'info')}
          >
            View Source Email
          </Button>
        )}
      </Stack>
    </Stack>
  );
}
